/**
 * Service catalog templates for profession-specific categories.
 *
 * Each template provides:
 *   - suggestedServices: pre-filled SERVICE items matching the ProductBuilder schema
 *   - coverage: UI-only object (BE support pending)
 *   - availability: UI-only object (BE support pending)
 *
 * Default Categories from the spec are intentionally omitted — they are handled
 * by the existing serviceSegment field in step 2.
 *
 * Template keys must match category ids (S####) in StoreCategoryList.json.
 */

import { GenerateShortID } from "./StoreUtils";

const uid = () => `tpl_${GenerateShortID(6)}`;

const uidForVariant = () => `tpl_var_${GenerateShortID(6)}`;
const uidForOption = () => `tpl_opt_${GenerateShortID(6)}`;
const uidForCustomisation = () => `tpl_cust_${GenerateShortID(6)}`;

function makeVariant(name, type, options, isRequired = true) {
  return {
    variantId: uidForVariant(),
    name,
    type,
    isRequired,
    Options: options.map((option, i) => {
      const normalized = typeof option === "string" ? { label: option } : option;
      return {
        optionId: uidForOption(),
        label: normalized.label,
        price: Number(normalized.price) || 0,
        addonPrice: normalized.addonPrice ?? true,
        isDefault: normalized.isDefault ?? i === 0,
      };
    }),
  };
}

function makeCustomization(name, type, options = {}) {
  return {
    customisationId: uidForCustomisation(),
    name,
    type,
    price: options.price ?? 0,
    addonPrice: options.addonPrice ?? true,
    DropdownOptions: options.DropdownOptions ?? [],
    acceptedFileTypes: type === "file" ? options.acceptedFileTypes ?? [] : [],
  };
}

function makeService(name, description, basePrice, variants = [], customisations = []) {
  return {
    ItemId: uid(),
    ItemName: name,
    Description: description,
    BasePrice: basePrice,
    Discount: 0,
    ItemType: "SERVICE",
    ItemImages: [],
    Variants: variants,
    Customisations: customisations,
  };
}

// ── Shared availability / coverage shapes (UI-only, BE pending) ────────────

const DEFAULT_AVAILABILITY = {
  days: [],      // e.g. ["Mon","Tue","Wed","Thu","Fri"]
};

// ── Templates ─────────────────────────────────────────────────────────────

export const SERVICE_CATALOG_TEMPLATES = {
  "S1017": {
    suggestedServices: [
      makeService(
        "Document Printing",
        "Black-and-white or color document printing. Price is per printed page.",
        5,
        [
          makeVariant("Print Type", "single", [
            { label: "Black & White", price: 5, addonPrice: false },
            { label: "Color", price: 10, addonPrice: false },
          ]),
          makeVariant("Paper Size", "single", ["A4"]),
        ],
        [
          makeCustomization("Upload Document", "file", { acceptedFileTypes: [".pdf", ".docx", ".jpg", ".jpeg", ".png"] }),
          makeCustomization("Printing Instructions", "text"),
          makeCustomization("Print on Both Sides", "boolean"),
        ]
      ),
      makeService(
        "Photo Printing",
        "Color photo printing in postcard or A4 size.",
        16,
        [
          makeVariant("Print Size", "single", [
            { label: 'Postcard (4" x 6")', price: 16, addonPrice: false },
            { label: "A4", price: 25, addonPrice: false },
          ]),
          makeVariant("Paper GSM", "single", ["180 GSM"]),
        ],
        [
          makeCustomization("Upload Photo", "file", { acceptedFileTypes: [".jpg", ".jpeg", ".png", ".pdf"] }),
          makeCustomization("Printing Instructions", "text"),
          makeCustomization("Contact Before Printing", "boolean"),
        ]
      ),
    ],
    availability: { ...DEFAULT_AVAILABILITY },
    coverage: {
      type: "local",
      notes: "Document Upload, Local Pickup, Local Delivery",
    },
  },

  "S1048": {
    suggestedServices: [
      makeService("Breakfast Cooking", "Fresh home-cooked breakfast prepared at your kitchen.", 300, [
        makeVariant("Meal Type", "single", ["Breakfast", "Lunch", "Dinner", "Snacks"]),
        makeVariant("Cuisine", "single", ["South Indian", "North Indian", "Chinese", "Continental", "Jain", "Vegan"]),
        makeVariant("PAX", "single", ["1-2", "3-4", "5-6", "7-10", "10+"]),
      ]),
      makeService("Lunch Cooking", "Wholesome lunch cooked fresh at your home.", 400, [
        makeVariant("Meal Type", "single", ["Breakfast", "Lunch", "Dinner", "Snacks"]),
        makeVariant("Cuisine", "single", ["South Indian", "North Indian", "Chinese", "Continental", "Jain", "Vegan"]),
        makeVariant("PAX", "single", ["1-2", "3-4", "5-6", "7-10", "10+"]),
      ]),
      makeService("Dinner Cooking", "Evening dinner prepared at your home by a professional chef.", 500, [
        makeVariant("Meal Type", "single", ["Breakfast", "Lunch", "Dinner", "Snacks"]),
        makeVariant("Cuisine", "single", ["South Indian", "North Indian", "Chinese", "Continental", "Jain", "Vegan"]),
        makeVariant("PAX", "single", ["1-2", "3-4", "5-6", "7-10", "10+"]),
      ]),
      makeService("Weekly Meal Prep", "Batch cooking and meal prep for the week.", 1800, [
        makeVariant("Cuisine", "single", ["South Indian", "North Indian", "Chinese", "Continental", "Jain", "Vegan"]),
        makeVariant("PAX", "single", ["1-2", "3-4", "5-6", "7-10", "10+"]),
      ]),
      makeService("Custom Cakes", "Made-to-order cakes for birthdays, anniversaries and special occasions.", 800, [
        makeVariant("PAX", "single", ["1-2", "3-4", "5-6", "7-10", "10+"]),
      ]),
      makeService("Cupcakes", "Freshly baked cupcakes in your choice of flavour.", 500, [
        makeVariant("PAX", "single", ["1-2", "3-4", "5-6", "7-10", "10+"]),
      ]),
    ],
    availability: { ...DEFAULT_AVAILABILITY },
    coverage: { type: "radius", notes: "Apartments, Communities, Radius" },
  },

  "S1049": {
    suggestedServices: [
      makeService("House Cleaning", "Full home cleaning service including sweeping, mopping and dusting.", 500, [
        makeVariant("Shift", "single", ["Morning", "Afternoon", "Evening", "Full Day"]),
        makeVariant("Service Type", "single", ["One-time", "Daily", "Weekly"]),
        makeVariant("Home Size", "single", ["1BHK", "2BHK", "3BHK", "Villa"]),
      ]),
      makeService("Utensil Cleaning", "Kitchen utensil and vessel cleaning.", 250, [
        makeVariant("Shift", "single", ["Morning", "Afternoon", "Evening"]),
        makeVariant("Service Type", "single", ["One-time", "Daily", "Weekly"]),
      ]),
      makeService("Laundry", "Washing, drying and folding of clothes.", 400, [
        makeVariant("Service Type", "single", ["One-time", "Daily", "Weekly"]),
        makeVariant("Home Size", "single", ["1BHK", "2BHK", "3BHK", "Villa"]),
      ]),
      makeService("Cooking Assistance", "Help with meal preparation and kitchen work.", 350, [
        makeVariant("Shift", "single", ["Morning", "Afternoon", "Evening", "Full Day"]),
        makeVariant("Service Type", "single", ["One-time", "Daily", "Weekly"]),
      ]),
      makeService("Elder Assistance", "Dedicated care and assistance for senior family members.", 600, [
        makeVariant("Shift", "single", ["Morning", "Afternoon", "Evening", "Full Day"]),
        makeVariant("Service Type", "single", ["One-time", "Daily", "Weekly"]),
      ]),
    ],
    availability: { ...DEFAULT_AVAILABILITY },
    coverage: { type: "radius", notes: "Apartments, Radius" },
  },

  "S1050": {
    suggestedServices: [
      makeService("Mathematics Tuition", "One-on-one or small group maths tutoring.", 600, [
        makeVariant("Subject", "single", ["Maths", "Science", "English", "Coding", "Music"]),
        makeVariant("Grade", "single", ["1-5", "6-8", "9-10", "11-12", "College"]),
        makeVariant("Mode", "single", ["Online", "At Student Home", "At Tutor Home"]),
      ]),
      makeService("Science Tuition", "Physics, Chemistry and Biology tutoring.", 600, [
        makeVariant("Subject", "single", ["Maths", "Science", "English", "Coding", "Music"]),
        makeVariant("Grade", "single", ["1-5", "6-8", "9-10", "11-12", "College"]),
        makeVariant("Mode", "single", ["Online", "At Student Home", "At Tutor Home"]),
      ]),
      makeService("English Tuition", "Grammar, comprehension and writing skills.", 500, [
        makeVariant("Grade", "single", ["1-5", "6-8", "9-10", "11-12", "College"]),
        makeVariant("Mode", "single", ["Online", "At Student Home", "At Tutor Home"]),
      ]),
      makeService("Coding Classes", "Programming fundamentals for school and college students.", 700, [
        makeVariant("Grade", "single", ["6-8", "9-10", "11-12", "College"]),
        makeVariant("Mode", "single", ["Online", "At Student Home", "At Tutor Home"]),
      ]),
      makeService("Guitar Lessons", "Beginner to advanced guitar training.", 800, [
        makeVariant("Mode", "single", ["Online", "At Student Home", "At Tutor Home"]),
      ]),
    ],
    availability: { ...DEFAULT_AVAILABILITY },
    coverage: { type: "mixed", notes: "Online, Local Area" },
  },

  "S1051": {
    suggestedServices: [
      makeService("Haircut", "Professional haircut at your home or salon.", 300, [
        makeVariant("Service Type", "single", ["Hair", "Skin", "Makeup", "Bridal"]),
        makeVariant("Gender", "single", ["Women", "Men", "Unisex"]),
        makeVariant("Location", "single", ["At Home", "At Salon"]),
      ]),
      makeService("Hair Spa", "Deep conditioning and relaxing hair spa treatment.", 600, [
        makeVariant("Gender", "single", ["Women", "Men", "Unisex"]),
        makeVariant("Location", "single", ["At Home", "At Salon"]),
      ]),
      makeService("Facial", "Cleansing and rejuvenating facial treatment.", 700, [
        makeVariant("Service Type", "single", ["Hair", "Skin", "Makeup", "Bridal"]),
        makeVariant("Gender", "single", ["Women", "Men", "Unisex"]),
        makeVariant("Location", "single", ["At Home", "At Salon"]),
      ]),
      makeService("Bridal Makeup", "Complete bridal makeup package for the special day.", 5000, [
        makeVariant("Service Type", "single", ["Hair", "Skin", "Makeup", "Bridal"]),
        makeVariant("Location", "single", ["At Home", "At Salon"]),
      ]),
      makeService("Party Makeup", "Glamorous makeup for parties and events.", 1500, [
        makeVariant("Service Type", "single", ["Hair", "Skin", "Makeup", "Bridal"]),
        makeVariant("Gender", "single", ["Women", "Men", "Unisex"]),
        makeVariant("Location", "single", ["At Home", "At Salon"]),
      ]),
    ],
    availability: { ...DEFAULT_AVAILABILITY },
    coverage: { type: "radius", notes: "Radius, Apartments" },
  },

  "S1052": {
    suggestedServices: [
      makeService("Airport Drop", "Comfortable and timely airport pickup or drop service.", 800, [
        makeVariant("Vehicle", "single", ["Car", "SUV", "Luxury"]),
        makeVariant("Trip Type", "single", ["One Way", "Round Trip"]),
      ]),
      makeService("Daily Driver", "Dedicated personal driver for your daily commute and errands.", 1200, [
        makeVariant("Shift", "single", ["Morning", "Evening", "Night", "Full Day"]),
        makeVariant("Vehicle", "single", ["Car", "SUV", "Luxury"]),
      ]),
      makeService("Outstation Trip", "Long-distance outstation travel with an experienced driver.", 2500, [
        makeVariant("Vehicle", "single", ["Car", "SUV", "Luxury"]),
        makeVariant("Trip Type", "single", ["One Way", "Round Trip"]),
      ]),
      makeService("Event Driver", "Professional driver service for weddings, parties and events.", 1500, [
        makeVariant("Vehicle", "single", ["Car", "SUV", "Luxury"]),
        makeVariant("Shift", "single", ["Morning", "Evening", "Night", "Full Day"]),
      ]),
    ],
    availability: { ...DEFAULT_AVAILABILITY },
    coverage: { type: "city", notes: "City, Radius" },
  },


  "S1053": {
    suggestedServices: [
      makeService("Griha Pravesh Pooja", "Traditional housewarming pooja.", 3500, [
        makeVariant("Samagri", "single", ["Included", "Customer Provides"]),
        makeVariant("Language", "single", ["Sanskrit", "Kannada", "Hindi", "Tamil", "Telugu"]),
      ]),
      makeService("Satyanarayan Pooja", "Complete Satyanarayan Katha and Pooja.", 3000, [
        makeVariant("Samagri", "single", ["Included", "Customer Provides"]),
        makeVariant("Language", "single", ["Sanskrit", "Kannada", "Hindi", "Tamil", "Telugu"]),
      ]),
      makeService("Birthday Pooja", "Birthday Ayush Homam and blessings.", 2500, [
        makeVariant("Samagri", "single", ["Included", "Customer Provides"]),
        makeVariant("Language", "single", ["Sanskrit", "Kannada", "Hindi", "Tamil", "Telugu"]),
      ]),
      makeService("Mundan Ceremony", "Traditional Mundan (Chudakarana) rituals.", 3500, [
        makeVariant("Samagri", "single", ["Included", "Customer Provides"]),
        makeVariant("Language", "single", ["Sanskrit", "Kannada", "Hindi"]),
      ]),
      makeService("Naming Ceremony", "Namakarana pooja for newborns.", 3000, [
        makeVariant("Samagri", "single", ["Included", "Customer Provides"]),
        makeVariant("Language", "single", ["Sanskrit", "Kannada", "Hindi"]),
      ]),
      makeService("Wedding Pooja", "Marriage rituals conducted by experienced priests.", 12000, [
        makeVariant("Duration", "single", ["Half Day", "Full Day"]),
        makeVariant("Language", "single", ["Sanskrit", "Kannada", "Hindi", "Tamil", "Telugu"]),
      ]),
      makeService("Lakshmi Pooja", "Prosperity and festival pooja at home.", 2500, [
        makeVariant("Samagri", "single", ["Included", "Customer Provides"]),
        makeVariant("Language", "single", ["Sanskrit", "Kannada", "Hindi"]),
      ]),
      makeService("Office Pooja", "Vastu and office inauguration pooja.", 4000, [
        makeVariant("Samagri", "single", ["Included", "Customer Provides"]),
        makeVariant("Language", "single", ["Sanskrit", "Kannada", "Hindi"]),
      ]),
    ],
    availability: { ...DEFAULT_AVAILABILITY },
    coverage: { type: "city", notes: "Home Visits, City" },
  },

};

/** Returns true if a service category has a template. */
export function hasServiceTemplate(serviceSegment) {
  return Boolean(serviceSegment && SERVICE_CATALOG_TEMPLATES[serviceSegment]);
}

/** Returns the template for the given service category, or null. */
export function getServiceTemplate(serviceSegment) {
  return SERVICE_CATALOG_TEMPLATES[serviceSegment] ?? null;
}
